/**
 * ExpiringContractsWidget — Dashboard-Kachel „Bald ablaufende Verträge"
 *
 * Listet befristete Verträge, die innerhalb der nächsten 30/60 Tage ablaufen
 * (oder bereits abgelaufen sind), RBAC-skopiert. Für HR/Standortleitung.
 * Versteckt sich, wenn nichts ansteht.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { FileSignature, ArrowRight, AlertTriangle } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { dashboardAPI } from '../../lib/api';
import { CONTRACT_TYPE_LABELS } from '../employee/ContractsTab';

const RANGES = [30, 60];

const fmt = (d) => {
  try { return format(parseISO(d), 'dd.MM.yyyy', { locale: de }); } catch { return d; }
};

const dayLabel = (n) => {
  if (n < 0) return `vor ${Math.abs(n)} Tag${Math.abs(n) === 1 ? '' : 'en'} abgelaufen`;
  if (n === 0) return 'läuft heute ab';
  return `in ${n} Tag${n === 1 ? '' : 'en'}`;
};

export const ExpiringContractsWidget = () => {
  const navigate = useNavigate();
  const [days, setDays] = useState(60);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    dashboardAPI.getExpiringContracts(days)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData({ items: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  const items = data?.items || [];

  // Versteckt sich komplett, wenn nichts ansteht (kein Clutter)
  if (!loading && items.length === 0) return null;

  return (
    <Card className="border-border/60" data-testid="expiring-contracts-widget">
      <CardHeader className="border-b border-border/40 pb-4">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <FileSignature className="h-5 w-5 text-primary" />
            Bald ablaufende Verträge
            {data?.total > 0 && (
              <Badge variant="secondary" className="ml-1">{data.total}</Badge>
            )}
          </CardTitle>
          <div className="flex gap-1">
            {RANGES.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setDays(r)}
                className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${
                  days === r ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted'
                }`}
                data-testid={`expiring-range-${r}`}
              >
                {r} Tage
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-3">
          {items.map((c) => {
            const expired = c.days_left < 0;
            const soon = c.days_left >= 0 && c.days_left <= 30;
            return (
              <div
                key={`${c.employee_id}-${c.contract_id}`}
                className="flex items-center justify-between gap-3 p-3 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors cursor-pointer"
                onClick={() => navigate(`/employees/${c.employee_id}`)}
                data-testid={`expiring-contract-${c.contract_id}`}
              >
                <div className="min-w-0">
                  <p className="font-medium truncate">{c.employee_name}</p>
                  <p className="text-sm text-muted-foreground truncate">
                    <Badge variant="outline" className="text-xs mr-1">{CONTRACT_TYPE_LABELS[c.contract_type] || 'Vertrag'}</Badge>
                    {c.title} · {c.location_name}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <Badge
                    className={`gap-1 ${expired
                      ? 'bg-destructive text-destructive-foreground'
                      : soon
                        ? 'bg-amber-500 text-white border-amber-600'
                        : ''}`}
                    variant={expired || soon ? undefined : 'outline'}
                  >
                    {(expired || soon) && <AlertTriangle className="h-3 w-3" />}
                    {dayLabel(c.days_left)}
                  </Badge>
                  <p className="text-xs text-muted-foreground mt-1">bis {fmt(c.valid_until)}</p>
                </div>
              </div>
            );
          })}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="mt-3 w-full text-primary"
          onClick={() => navigate('/employees')}
          data-testid="expiring-contracts-all-btn"
        >
          Zu den Mitarbeitern <ArrowRight className="ml-1 h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
};

export default ExpiringContractsWidget;
